import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { toCard } from '@/lib/article-card';
import {
    getArticlePageBundle,
    getAuthorById,
    getCategoryByKey,
    getDailyGalleryArticle,
    getTodayCrossword,
    getWeeklyMostReadArticles,
    getHomepageFrontpageSections,
    getPublishedArticlesByIds,
    getWeather,
    categoriesByKey,
    listActiveAdsByPlacement,
    listActiveAdsByPlacements,
    listArticlesByAuthor,
    listArticlesByCategory,
    listArticlesByCategoryPools,
    listAuthorsForSitemap,
    listCategories,
    listCategoriesForSitemap,
    listNationalAshkelonAlerts,
    listNationalTopAlerts,
    listNewsArticlesForSitemap,
    listPublishedArticlesForSitemap,
    listRecentArticlesForFeed,
    listSeaForecasts,
    listWeatherCities,
    listCinemaScreenings,
    listCinemaScreeningsForHome,
    contentImageFileId,
    featuredCoverPreviewUrl,
    momentCoverPreviewUrl,
} from '@/server/content';
import type {
    Ad,
    Article as DomainArticle,
    ExternalAlert,
    SeaForecastPeriod,
    Weather,
    CinemaScreening,
} from '@/lib/content-types';
import type { CrosswordPayload } from '@/lib/ashkelon-crossword';
import { resolveHomepageLayout } from '@/lib/frontpage';
import { PRIMARY_WEATHER_CITY } from '@/lib/weather-cities';
import { getAshkelonShabbatTimes } from '@/lib/shabbat';
import { searchArticleVectors } from '@/server/lib/vectors';

const CATEGORY_FETCH_BUFFER = 24;
const HOMEPAGE_CATEGORY_POOLS = [
    { key: 'news', limit: CATEGORY_FETCH_BUFFER },
    { key: 'crime', limit: CATEGORY_FETCH_BUFFER },
    { key: 'sports', limit: CATEGORY_FETCH_BUFFER },
    { key: 'transport', limit: CATEGORY_FETCH_BUFFER },
    { key: 'business', limit: CATEGORY_FETCH_BUFFER },
    { key: 'education', limit: CATEGORY_FETCH_BUFFER },
    { key: 'entertainment', limit: CATEGORY_FETCH_BUFFER },
] as const;
const FEATURED_DISPLAY = 7;
const CATEGORY_DISPLAY = 6;
/** Spotlight: 1 lead + 4 compact stack. */
const SPORTS_DISPLAY = 5;
/** Transport board: 1 full-bleed lead + 2 headline strip. */
const TRANSPORT_BOARD_DISPLAY = 3;
const MORE_DISPLAY = 3;

function takeUnseen(
    pool: DomainArticle[],
    count: number,
    seen: Set<string>,
): DomainArticle[] {
    const picked: DomainArticle[] = [];
    for (const article of pool) {
        if (picked.length >= count) break;
        if (seen.has(article.$id)) continue;
        picked.push(article);
        seen.add(article.$id);
    }
    return picked;
}

/** Prefer unseen articles, then backfill from the same pool to fill fixed layouts. */
function takeUnseenWithBackfill(
    pool: DomainArticle[],
    count: number,
    seen: Set<string>,
): DomainArticle[] {
    const picked = takeUnseen(pool, count, seen);
    if (picked.length >= count) return picked;

    const pickedIds = new Set(picked.map((article) => article.$id));
    for (const article of pool) {
        if (picked.length >= count) break;
        if (pickedIds.has(article.$id)) continue;
        picked.push(article);
        pickedIds.add(article.$id);
        if (!seen.has(article.$id)) seen.add(article.$id);
    }
    return picked;
}

function mapCategoryCardsWithLeadMomentCover(articles: DomainArticle[]) {
    return articles.map((article, index) => {
        const card = toCard(article);
        if (index !== 0) return card;
        const covers = (article.heroImages ?? [])
            .map((img) =>
                momentCoverPreviewUrl(contentImageFileId(img))
                ?? img.coverUrl
                ?? img.thumbUrl
                ?? img.url,
            )
            .filter((url): url is string => !!url);
        const cover = covers[0] ?? article.coverUrl ?? card.cover;
        if (!cover) return card;
        return {
            ...card,
            cover,
            covers: covers.length > 0 ? covers : [cover],
        };
    });
}

export type HomePageDeferredData = {
    cinemaScreenings: CinemaScreening[];
    alerts: ExternalAlert[];
    talkingAboutUs: ExternalAlert[];
    ads: Ad[];
    leaderboardAds: Ad[];
    dailyPhoto: ReturnType<typeof toCard> | null;
    crossword: CrosswordPayload | null;
    weeklyTop: { article: ReturnType<typeof toCard>; views: number }[];
    weather: Weather | null;
    sea: SeaForecastPeriod[];
};

const EMPTY_HOME_DEFERRED: HomePageDeferredData = {
    cinemaScreenings: [],
    alerts: [],
    talkingAboutUs: [],
    ads: [],
    leaderboardAds: [],
    dailyPhoto: null,
    crossword: null,
    weeklyTop: [],
    weather: null,
    sea: [],
};

/** Featured story + category rails. Blocks first paint. */
export const loadHomePage = createServerFn({ method: 'GET' }).handler(async () => {
    const [
        categoriesMap,
        { home, more },
        categoryPools,
    ] = await Promise.all([
        categoriesByKey(),
        getHomepageFrontpageSections(),
        listArticlesByCategoryPools(HOMEPAGE_CATEGORY_POOLS),
    ]);

    const newsCat = categoriesMap.get('news');
    const crimeCat = categoriesMap.get('crime');
    const sportsCat = categoriesMap.get('sports');
    const transportCat = categoriesMap.get('transport');
    const businessCat = categoriesMap.get('business');
    const educationCat = categoriesMap.get('education');
    const entertainmentCat = categoriesMap.get('entertainment');
    const newsPool = categoryPools.get('news') ?? [];
    const crimePool = categoryPools.get('crime') ?? [];
    const sportsPool = categoryPools.get('sports') ?? [];
    const transportPool = categoryPools.get('transport') ?? [];
    const businessPool = categoryPools.get('business') ?? [];
    const educationPool = categoryPools.get('education') ?? [];
    const entertainmentPool = categoryPools.get('entertainment') ?? [];

    const seen = new Set<string>();
    const featured = takeUnseen(home?.articles ?? [], FEATURED_DISPLAY, seen);
    // Claim transport before broader news/sports pools - many transport items are multi-tagged.
    const transportArticles = takeUnseenWithBackfill(
        transportPool,
        TRANSPORT_BOARD_DISPLAY,
        seen,
    );
    const newsArticles = takeUnseen(newsPool, CATEGORY_DISPLAY, seen);
    const sportsArticles = takeUnseen(sportsPool, SPORTS_DISPLAY, seen);
    const crimeArticles = takeUnseen(crimePool, 3, seen);
    const businessArticles = takeUnseen(businessPool, CATEGORY_DISPLAY, seen);
    const educationArticles = takeUnseen(educationPool, CATEGORY_DISPLAY, seen);
    const entertainmentArticles = takeUnseen(entertainmentPool, CATEGORY_DISPLAY, seen);
    const moreInAshkelon = takeUnseen(more?.articles ?? [], MORE_DISPLAY, seen);

    // Featured hero fills ~column width × --homepage-tall-ad-h (~16:9).
    const featuredCards = featured.map((article, index) => {
        const card = toCard(article);
        if (index === 0) {
            const covers = (article.heroImages ?? [])
                .map((img) =>
                    featuredCoverPreviewUrl(contentImageFileId(img))
                    ?? img.coverUrl
                    ?? img.thumbUrl
                    ?? img.url,
                )
                .filter((url): url is string => !!url);
            const cover = covers[0] ?? article.coverUrl ?? card.cover;
            if (cover) {
                return {
                    ...card,
                    cover,
                    covers: covers.length > 0 ? covers : [cover],
                };
            }
        }
        return card;
    });

    // Full-bleed category rails use the large moment preview on the lead, not card thumbs.
    const transportCards = mapCategoryCardsWithLeadMomentCover(transportArticles);
    const entertainmentCards = mapCategoryCardsWithLeadMomentCover(entertainmentArticles);

    return {
        featured: featuredCards,
        news: { cat: newsCat, articles: newsArticles.map(toCard) },
        crime: { cat: crimeCat, articles: crimeArticles.map(toCard) },
        sports: { cat: sportsCat, articles: sportsArticles.map(toCard) },
        transport: { cat: transportCat, articles: transportCards },
        business: { cat: businessCat, articles: businessArticles.map(toCard) },
        education: { cat: educationCat, articles: educationArticles.map(toCard) },
        entertainment: { cat: entertainmentCat, articles: entertainmentCards },
        more: { title: more?.title ?? 'עוד באשקלון', articles: moreInAshkelon.map(toCard) },
        layoutMode: resolveHomepageLayout(home?.layout),
    };
});

/** Below-the-fold / sidebar homepage data. Streamed after the article rails. */
export const loadHomePageDeferred = createServerFn({ method: 'GET' }).handler(
    async (): Promise<HomePageDeferredData> => {
        try {
            const [
                galleryArticle,
                crossword,
                weeklyMostRead,
                homepageAds,
                nationalTopAlerts,
                talkingAboutUs,
                cinemaScreenings,
                weather,
                sea,
            ] = await Promise.all([
                getDailyGalleryArticle('gallery').catch(() => undefined),
                getTodayCrossword().catch(() => null),
                getWeeklyMostReadArticles(10).catch(() => []),
                listActiveAdsByPlacements(['homepage-sidebar', 'homepage-leaderboard'])
                    .catch(() => ({} as Awaited<ReturnType<typeof listActiveAdsByPlacements>>)),
                listNationalTopAlerts(20).catch(() => []),
                listNationalAshkelonAlerts(5).catch(() => []),
                listCinemaScreeningsForHome().catch(() => []),
                getWeather(PRIMARY_WEATHER_CITY).catch(() => null),
                listSeaForecasts().catch(() => []),
            ]);

            const dailyPhotoCovers = (galleryArticle?.heroImages ?? [])
                .map((img) =>
                    momentCoverPreviewUrl(contentImageFileId(img))
                    ?? img.coverUrl
                    ?? img.thumbUrl
                    ?? img.url,
                )
                .filter((url): url is string => !!url);
            const dailyPhotoCover =
                dailyPhotoCovers[0]
                ?? galleryArticle?.coverUrl
                ?? galleryArticle?.coverThumbUrl
                ?? '';
            const dailyPhoto = galleryArticle && dailyPhotoCover
                ? {
                    ...toCard(galleryArticle),
                    cover: dailyPhotoCover,
                    covers: dailyPhotoCovers.length > 0 ? dailyPhotoCovers : [dailyPhotoCover],
                }
                : null;

            return {
                cinemaScreenings,
                alerts: nationalTopAlerts,
                talkingAboutUs,
                ads: homepageAds['homepage-sidebar'] ?? [],
                leaderboardAds: homepageAds['homepage-leaderboard'] ?? [],
                dailyPhoto,
                crossword,
                weeklyTop: weeklyMostRead.map((row) => ({
                    article: toCard(row.article),
                    views: row.views,
                })),
                weather,
                sea,
            };
        } catch {
            return EMPTY_HOME_DEFERRED;
        }
    },
);

export const loadArticlePage = createServerFn({ method: 'GET' })
    .validator((id: string) => id)
    .handler(async ({ data: id }) => {
        const [bundle, articleAds] = await Promise.all([
            getArticlePageBundle(id),
            listActiveAdsByPlacements(['article-inline', 'article-sidebar']),
        ]);
        if (!bundle) return null;
        return {
            article: bundle.article,
            related: bundle.related.map(toCard),
            latest: bundle.latest.map(toCard),
            inlineAds: articleAds['article-inline'] ?? [],
            sidebarAds: articleAds['article-sidebar'] ?? [],
            /** True when the request used a legacy `/article/{number}` param. */
            needsCanonicalRedirect: bundle.needsCanonicalRedirect,
        };
    });

export const loadCategoryPage = createServerFn({ method: 'GET' })
    .validator((key: string) => key)
    .handler(async ({ data: key }) => {
        const category = await getCategoryByKey(key);
        if (!category) return null;
        const [articles, ads] = await Promise.all([
            listArticlesByCategory(key, 48),
            listActiveAdsByPlacement('category-inline'),
        ]);
        return { category, articles: articles.map(toCard), ads };
    });

export const loadAuthorPage = createServerFn({ method: 'GET' })
    .validator((id: string) => id)
    .handler(async ({ data: id }) => {
        const author = await getAuthorById(id);
        if (!author) return null;
        const articles = await listArticlesByAuthor(id, 48);
        return { author, articles: articles.map(toCard) };
    });

export const loadSearchPage = createServerFn({ method: 'GET' })
    .validator(z.object({ q: z.string().optional() }))
    .handler(async ({ data }) => {
        const q = data.q?.trim() ?? '';
        if (!q) {
            return { q: '', articles: [] as ReturnType<typeof toCard>[] };
        }

        try {
            const hits = await searchArticleVectors(q, 24);
            const articles = await getPublishedArticlesByIds(
                hits.map((hit) => hit.id),
            );
            return { q, articles: articles.map(toCard) };
        } catch (error) {
            console.error('[search] vector query failed', error);
            return { q, articles: [] as ReturnType<typeof toCard>[] };
        }
    });

export const loadSitemapArticles = createServerFn({ method: 'GET' }).handler(
    async () => listPublishedArticlesForSitemap(),
);

export const loadSitemapNews = createServerFn({ method: 'GET' }).handler(
    async () => listNewsArticlesForSitemap(),
);

export const loadSitemapCategories = createServerFn({ method: 'GET' }).handler(
    async () => listCategoriesForSitemap(),
);

export const loadSitemapAuthors = createServerFn({ method: 'GET' }).handler(
    async () => listAuthorsForSitemap(),
);

export const loadRssFeed = createServerFn({ method: 'GET' }).handler(
    async () => listRecentArticlesForFeed(50),
);

/** Categories + recent articles for `/llms.txt` and `/llms-full.txt`. */
export const loadLlmsIndex = createServerFn({ method: 'GET' })
    .validator(z.object({ articleLimit: z.number().int().min(1).max(50).optional() }))
    .handler(async ({ data }) => {
        const limit = data.articleLimit ?? 12;
        const [categories, articles] = await Promise.all([
            listCategories().catch(() => []),
            listRecentArticlesForFeed(limit).catch(() => []),
        ]);
        return {
            categories: categories.map((c) => ({ key: c.key, name: c.name })),
            recentArticles: articles.map((a) => ({
                title: a.title,
                slug: a.slug,
                date: a.date,
                subtitle: a.subtitle ?? null,
            })),
        };
    });

/** Site weather for the homepage strip. */
export const loadWeather = createServerFn({ method: 'GET' }).handler(async () => {
    const [weather, sea] = await Promise.all([
        getWeather(PRIMARY_WEATHER_CITY).catch(() => null),
        listSeaForecasts().catch(() => []),
    ]);
    return { weather, sea };
});

/** Ashkelon Shabbat entrance/exit for the Israeli weekend block (root loader). */
export const loadShabbatEntrance = createServerFn({ method: 'GET' }).handler(
    async () => {
        try {
            return { shabbatTimes: getAshkelonShabbatTimes() };
        } catch {
            return { shabbatTimes: null };
        }
    },
);

/** Active categories for site nav (header, footer, sidebar). */
export const loadNavCategories = createServerFn({ method: 'GET' }).handler(
    async () => {
        try {
            const categories = await listCategories();
            return {
                navCategories: categories.map((c) => ({
                    id: c.$id,
                    key: c.key,
                    name: c.name,
                })),
            };
        } catch {
            return { navCategories: [] as Array<{ id: string; key: string; name: string }> };
        }
    },
);

/** Dedicated /weather page: primary city + all synced cities. */
export const loadWeatherPage = createServerFn({ method: 'GET' }).handler(async () => {
    const [cities, sea] = await Promise.all([
        listWeatherCities().catch(() => []),
        listSeaForecasts().catch(() => []),
    ]);
    const primary =
        cities.find((c) => c.locationKey === PRIMARY_WEATHER_CITY)
        ?? cities[0]
        ?? null;
    return { primary, cities, sea };
});

/** Dedicated /cinema page: upcoming screenings from synced chains. */
export const loadCinemaPage = createServerFn({ method: 'GET' }).handler(async () => {
    const screenings = await listCinemaScreenings().catch(() => []);
    return { screenings };
});
